/**
 * Phase 3: TypeScript 타입 시스템 통합
 * 
 * booking_status enum을 위한 타입 안전한 헬퍼 함수들
 * 
 * Database enum values:
 * - pending: 결제 대기
 * - confirmed: 예약 완료 (결제 완료) 
 * - cancelled: 예약 취소
 */

import { Database } from "@/integrations/supabase/types";

// 편의성을 위한 타입 별칭
export type BookingStatus = Database["public"]["Enums"]["booking_status"];

// 상수 정의 - Database에서 자동 추출된 값과 일치 보장
export const BOOKING_STATUS = {
  PENDING: "pending" as BookingStatus,
  CONFIRMED: "confirmed" as BookingStatus, 
  CANCELLED: "cancelled" as BookingStatus,
} as const;

// 유효한 상태값들의 배열 (컴파일 타임에 검증)
export const BOOKING_STATUS_VALUES: BookingStatus[] = [
  BOOKING_STATUS.PENDING,
  BOOKING_STATUS.CONFIRMED,
  BOOKING_STATUS.CANCELLED,
] as const;

/**
 * 주어진 문자열이 유효한 booking_status인지 확인
 */
export function isValidBookingStatus(status: string): status is BookingStatus {
  return BOOKING_STATUS_VALUES.includes(status as BookingStatus);
}

/**
 * 안전한 상태 변환 - 유효하지 않은 값에 대해서는 default 반환
 */
export function parseBookingStatus(
  status: string | null | undefined, 
  defaultStatus: BookingStatus = BOOKING_STATUS.PENDING
): BookingStatus {
  if (!status) return defaultStatus;
  return isValidBookingStatus(status) ? status : defaultStatus;
}

/**
 * 상태 전이 유효성 검사
 */
export function isValidStatusTransition(
  currentStatus: BookingStatus,
  newStatus: BookingStatus
): boolean {
  const validTransitions: Record<BookingStatus, BookingStatus[]> = {
    [BOOKING_STATUS.PENDING]: [BOOKING_STATUS.CONFIRMED, BOOKING_STATUS.CANCELLED],
    [BOOKING_STATUS.CONFIRMED]: [BOOKING_STATUS.CANCELLED], // 확정 후에는 취소만 가능
    [BOOKING_STATUS.CANCELLED]: [], // 취소 후에는 상태 변경 불가
  };

  return validTransitions[currentStatus]?.includes(newStatus) ?? false;
}

/**
 * 상태별 한국어 라벨 
 */
export function getBookingStatusLabel(status: BookingStatus): string {
  const labels: Record<BookingStatus, string> = {
    [BOOKING_STATUS.PENDING]: "결제 대기",
    [BOOKING_STATUS.CONFIRMED]: "예약 완료",
    [BOOKING_STATUS.CANCELLED]: "예약 취소",
  };

  return labels[status];
}

/**
 * 상태별 색상 클래스 (Tailwind CSS)
 */
export function getBookingStatusColor(status: BookingStatus): {
  bg: string;
  text: string;
  border: string;
} {
  const colors: Record<BookingStatus, { bg: string; text: string; border: string }> = {
    [BOOKING_STATUS.PENDING]: {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      border: "border-yellow-200",
    },
    [BOOKING_STATUS.CONFIRMED]: {
      bg: "bg-green-50", 
      text: "text-green-700",
      border: "border-green-200",
    },
    [BOOKING_STATUS.CANCELLED]: {
      bg: "bg-gray-50",
      text: "text-gray-700", 
      border: "border-gray-200",
    },
  };

  return colors[status];
}

/**
 * 사용자가 수정 가능한 상태인지 확인
 */
export function canUserModifyBooking(status: BookingStatus): boolean {
  return status === BOOKING_STATUS.PENDING || status === BOOKING_STATUS.CONFIRMED;
}

/**
 * 결제 상태인지 확인 (결제 완료된 예약)
 */
export function isPaidBooking(status: BookingStatus): boolean {
  return status === BOOKING_STATUS.CONFIRMED;
}

/**
 * 취소 가능한 상태인지 확인
 */
export function canCancelBooking(status: BookingStatus): boolean {
  return status === BOOKING_STATUS.PENDING || status === BOOKING_STATUS.CONFIRMED;
}

/**
 * 관리자용 상태 변경 헬퍼
 */
export function getAvailableStatusTransitions(
  currentStatus: BookingStatus
): { value: BookingStatus; label: string }[] {
  const validNextStatuses = BOOKING_STATUS_VALUES.filter(status => 
    isValidStatusTransition(currentStatus, status)
  );

  return validNextStatuses.map(status => ({
    value: status,
    label: getBookingStatusLabel(status),
  }));
}

/**
 * 타입 가드: Database 타입과 일치하는지 확인
 */
export function assertBookingStatus(
  status: unknown
): asserts status is BookingStatus {
  if (typeof status !== "string" || !isValidBookingStatus(status)) {
    throw new Error(`Invalid booking status: ${status}`);
  }
}

/**
 * 개발용 유틸리티: 모든 enum 값이 처리되었는지 컴파일 타임에 확인
 */
export function exhaustiveCheck(status: never): never {
  throw new Error(`Unhandled booking status: ${status}`);
}